import React, { useState, useContext, useEffect } from "react";
import moment from "moment";
import { toast } from "../services/toast";

import {
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import { makeStyles } from 'tss-react/mui';

import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import AccountCircle from "@mui/icons-material/AccountCircle";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import TranslateIcon from "@mui/icons-material/Translate";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpOutlined from "@mui/icons-material/VolumeUpOutlined";
import VolumeOffOutlined from "@mui/icons-material/VolumeOffOutlined";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import ColorModeContext from "../context/ColorMode/ColorModeContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";

import { socketConnection } from "../services/socket";
import ChatPopover from "../pages/Chat/ChatPopover";
import useSettings from "../hooks/useSettings";
import api from "../services/api";
import PageHelp from "../components/PageHelp";

const drawerWidth = 270;
const ACTIVITY_EVENTS = ["pointerdown", "keydown"];
const ACTIVITY_REPORT_INTERVAL_MS = 60 * 1000;

const languageOptions = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
];

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down('md')]: {
      height: "calc(100vh - 56px)",
    },
  },

  toolbar: {
    paddingRight: 24,
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 8px",
    minHeight: "48px",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    backgroundImage: "linear-gradient(90deg, #a13a3a, #7a2c2c)",
    boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  logoChip: {
    display: "flex",
    alignItems: "center",
    padding: "3px 10px",
    marginRight: theme.spacing(2),
  },
  appBarActions: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    marginLeft: "auto",
  },
  avatarButton: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.92)",
    color: "#a13a3a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
    marginLeft: theme.spacing(1),
    transition: "transform 0.15s ease",
    "&:hover": {
      transform: "scale(1.08)",
    },
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    fontSize: 14,
  },
  drawerPaper: {
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: "#1c2128",
    color: "rgba(255,255,255,0.85)",
    borderRight: "none",
    "& .MuiDivider-root": {
      borderColor: "rgba(255,255,255,0.08)",
    },
    "& ::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(255,255,255,0.18)",
    },
    "& ::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "rgba(255,255,255,0.3)",
    },
  },
  drawerPaperPermanent: {
    position: "relative",
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
    "& .MuiListItemText-root": {
      display: "none",
    },
    "& .MuiListSubheader-root": {
      display: "none",
    },
    "& .groupChevron": {
      display: "none",
    },
    "& .MuiCollapse-root": {
      display: "none",
    },
    "& .MuiListItemButton-root": {
      justifyContent: "center",
      paddingLeft: 12,
      paddingRight: 12,
    },
    "& .MuiListItemIcon-root": {
      minWidth: "0 !important",
      justifyContent: "center",
    },
  },
  appBarSpacer: {
    minHeight: "48px",
  },
  content: {
    flex: 1,
    overflow: "auto",
    ...theme.scrollbarStyles,
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  containerWithScroll: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const LoggedInLayout = ({ children }) => {
  const { classes, cx } = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const langMenuOpen = Boolean(langAnchorEl);
  const { handleLogout, loading } = useContext(AuthContext);
  const { mode, toggleColorMode } = useContext(ColorModeContext);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const { user } = useContext(AuthContext);
  const { getAll: getAllSettings } = useSettings();
  const [soundMuted, setSoundMuted] = useState(
    () => localStorage.getItem("notificationSoundMuted") === "1"
  );

  const toggleSoundMuted = () => {
    setSoundMuted((prev) => {
      const next = !prev;
      localStorage.setItem("notificationSoundMuted", next ? "1" : "0");
      return next;
    });
  };





  const [logo, setLogo] = useState(`${process.env.REACT_APP_BACKEND_URL}/public/logos/logo.png`);

  const theme = useTheme();
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));


  useEffect(() => {
    async function findData() {

      const settingList = await getAllSettings();
      if (Array.isArray(settingList)) {
        const findLogo = settingList.find(
          (d) => d.key === "logo"
        );
        if (findLogo && findLogo.value) {
          setLogo(`${process.env.REACT_APP_BACKEND_URL}/public/logos/${findLogo.value}`)
        }
      }

    }
    findData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (document.body.offsetWidth < 1200) {
      setDrawerOpen(false);
    }
  }, []);

  useEffect(() => {
    const width = document.body.offsetWidth;
    if (width < 600) {
      setDrawerVariant("temporary");
    } else if (drawerOpen && width < 1200) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const userId = localStorage.getItem("userId");

    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-auth`, (data) => {
      if (data.user.id === +userId) {
        toastError("Han ingresado desde otra computadora a su cuenta.");
        setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 1000);
      }
    });

    socket.on(`company-${companyId}-appointment-reminder`, (data) => {
      const { appointment } = data;
      if (appointment.userId !== +userId) return;

      const hora = moment(appointment.startAt).format("HH:mm");
      toast.info(
        `Cita con ${appointment.contactName || "un contacto"} hoy a las ${hora}: "${appointment.title}"`,
        { duration: 15000 }
      );
    });

    let lastActivitySent = 0;
    const reportActivity = () => {
      const now = Date.now();
      if (now - lastActivitySent < ACTIVITY_REPORT_INTERVAL_MS) return;
      lastActivitySent = now;
      api.post("/users/activity").catch(() => {});
    };
    reportActivity();
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, reportActivity, { passive: true }));

    return () => {
      socket.disconnect();
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, reportActivity));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const handleOpenLangMenu = (event) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleCloseLangMenu = () => {
    setLangAnchorEl(null);
  };

  const handleChangeLanguage = (lng) => {
    handleCloseLangMenu();
    if (lng === i18n.language) return;
    i18n.changeLanguage(lng);
    window.location.reload();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: cx(
            classes.drawerPaper,
            drawerVariant === "permanent" && classes.drawerPaperPermanent,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <div className={classes.toolbarIcon}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              paddingLeft: 12,
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #c05f5f, #7a2c2c)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              {(user?.name || "?").charAt(0).toUpperCase()}
            </div>
            <span
              style={{
                fontWeight: 600,
                fontSize: 14,
                color: "#fff",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user.name}
            </span>
          </div>

          <IconButton
            onClick={() => setDrawerOpen(!drawerOpen)}
            size="small"
            sx={{ color: "rgba(255,255,255,0.6)" }}
          >
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        <List className={classes.containerWithScroll}>
          <MainListItems drawerClose={drawerClose} />
        </List>
        <Divider />
      </Drawer>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <AppBar
        position="absolute"
        className={cx(classes.appBar, drawerOpen && classes.appBarShift)}
        color="primary"
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={cx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden
            )}
            size="medium">
            <MenuIcon style={{ color: 'white' }} />
          </IconButton>

          <div className={classes.logoChip}>
            <img src={logo} height={26} alt="Logo" loading="lazy" />
          </div>

          <div className={classes.appBarActions}>
            <IconButton
              onClick={handleOpenLangMenu}
              size="medium"
              title={i18n.t("mainDrawer.appBar.language.title")}
              sx={{ color: "rgba(255,255,255,0.85)" }}
            >
              <TranslateIcon />
            </IconButton>
            <Menu
              anchorEl={langAnchorEl}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={langMenuOpen}
              onClose={handleCloseLangMenu}
            >
              {languageOptions.map(({ code, label }) => {
                const active =
                  i18n.language === code || i18n.language?.startsWith(`${code}-`);
                return (
                  <MenuItem
                    key={code}
                    onClick={() => handleChangeLanguage(code)}
                    style={{ fontSize: 14, fontWeight: active ? 600 : 400 }}
                  >
                    {active && (
                      <CheckIcon
                        fontSize="small"
                        style={{ marginRight: 8, color: "#3a8b3a" }}
                      />
                    )}
                    <span style={{ marginLeft: active ? 0 : 28 }}>{label}</span>
                  </MenuItem>
                );
              })}
            </Menu>
            <IconButton
              onClick={toggleColorMode}
              size="medium"
              title={mode === "light" ? "Modo oscuro" : "Modo claro"}
              sx={{ color: "rgba(255,255,255,0.85)" }}
            >
              {mode === "light" ? <Brightness4Icon /> : <Brightness7Icon />}
            </IconButton>
            <IconButton
              onClick={toggleSoundMuted}
              size="medium"
              title={soundMuted ? "Activar sonido de notificaciones" : "Silenciar sonido de notificaciones"}
              sx={{ color: "rgba(255,255,255,0.85)" }}
            >
              {soundMuted ? <VolumeOffOutlined /> : <VolumeUpOutlined />}
            </IconButton>
            {user.id && <NotificationsPopOver showBell={false} />}
            <AnnouncementsPopover />
            <ChatPopover />

            <div
              className={classes.avatarButton}
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              title={user?.name}
            >
              {(user?.name || "?").charAt(0).toUpperCase()}
            </div>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <div style={{ padding: "10px 16px 8px", minWidth: 200 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {user?.name}
                </div>
                <div style={{ fontSize: 12, color: "#5f6b76" }}>
                  {user?.email}
                </div>
              </div>
              <Divider />
              <MenuItem onClick={handleOpenUserModal} style={{ fontSize: 14, marginTop: 4 }}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem
                onClick={handleClickLogout}
                style={{ fontSize: 14, color: "#a13a3a" }}
              >
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />

        {children ? children : null}
        <PageHelp />
      </main>
    </div>
  );
};

export default LoggedInLayout;
